import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import SplashScreen from "./components/SplashScreen";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CarePage from "./pages/CarePage";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Coaches = lazy(() => import("./pages/Coaches"));
const Feed = lazy(() => import("./pages/Feed"));
const Workouts = lazy(() => import("./pages/Workouts"));
const WorkoutLogger = lazy(() => import("./pages/WorkoutLogger"));
const Nutrition = lazy(() => import("./pages/Nutrition"));
const WeightTracker = lazy(() => import("./pages/WeightTracker"));
const Wellness = lazy(() => import("./pages/Wellness"));
const CycleTracker = lazy(() => import("./pages/CycleTracker"));
const Wearables = lazy(() => import("./pages/Wearables"));
const Passport = lazy(() => import("./pages/Passport"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Challenges = lazy(() => import("./pages/Challenges"));
const Badges = lazy(() => import("./pages/Badges"));
const Community = lazy(() => import("./pages/Community"));
const Partner = lazy(() => import("./pages/Partner"));
const Pregnancy = lazy(() => import("./pages/Pregnancy"));
const FamilyPlan = lazy(() => import("./pages/FamilyPlan"));
const Referrals = lazy(() => import("./pages/Referrals"));
const HallOfFame = lazy(() => import("./pages/HallOfFame"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Subscription = lazy(() => import("./pages/Subscription"));
const MedicalProfile = lazy(() => import("./pages/MedicalProfile"));
const Profile = lazy(() => import("./pages/Profile"));
const Admin = lazy(() => import("./pages/Admin"));
const Legal = lazy(() => import("./pages/Legal"));
const Contact = lazy(() => import("./pages/Contact"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
import { initPush } from "./lib/push";
import { isNativeApp } from "./lib/platform";
import { enableWebPush, pushSupported } from "./lib/webPush";
import api from "./lib/api";
import { toast } from "sonner";
import { Sidebar, TopBar, BottomTabBar, MobileMenu, QuickAddSheet, DesktopQuickAddButton } from "./components/app/Shell";
import { UpgradeGate } from "./components/app/UpgradeGate";
import { QuickLogModal } from "./components/app/QuickLogModal";
import { WelcomeIntro, hasSeenIntro } from "./components/app/WelcomeIntro";
import { Toaster } from "./components/ui/sonner";

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center" data-testid="page-loading">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
    </div>
  );
}

const PUBLIC = ["landing", "login", "signup"];
const hashPage = () => window.location.hash.replace("#", "") || "landing";

function AppContent() {
  const { member, loading } = useAuth();
  const [showSplash, setShowSplash] = useState(!sessionStorage.getItem("aeva_splash_shown"));
  const [page, setPage] = useState(hashPage());
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [quickAdd, setQuickAdd] = useState(false);
  const [quickLog, setQuickLog] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showIntro, setShowIntro] = useState(false);
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token"));
  const [careToken] = useState(() => (window.location.pathname.match(/^\/care\/([A-Za-z0-9]+)/) || [])[1] || null);
  const [checkoutSession] = useState(() => new URLSearchParams(window.location.search).get("session_id"));
  const [stravaCode] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("strava") === "connected" ? p.get("code") : null;
  });

  useEffect(() => {
    if (!stravaCode) return;
    api.stravaCallback(stravaCode)
      .then((r) => { toast.success("Strava connected!", { description: r.athlete }); })
      .catch((e) => toast.error("Strava connect failed", { description: e.message }))
      .finally(() => { window.history.replaceState(null, "", window.location.pathname); navigate("wearables"); });
  }, [stravaCode]);

  useEffect(() => {
    if (member && !hasSeenIntro() && !(member.dismissedNudges || []).includes("intro")) setShowIntro(true);
  }, [member]);

  useEffect(() => {
    const onPop = () => setPage(hashPage());
    window.addEventListener("popstate", onPop);
    // capture referral code from ?ref= for signup attribution
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) sessionStorage.setItem("wyvn_ref", ref);
    // capture partner invite code from ?partner= for one-tap connect
    const partnerCode = new URLSearchParams(window.location.search).get("partner");
    if (partnerCode) sessionStorage.setItem("wyvn_partner_code", partnerCode);
    initPush(); // registers for native push on iOS/Android; no-op on web
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Auto-enable push for signed-in members who haven't opted out (best-effort, once per session).
  useEffect(() => {
    if (!member || member.pushEnabled === false) return;
    if (!pushSupported() || typeof Notification === "undefined") return;
    if (Notification.permission === "denied") return;
    if (sessionStorage.getItem("wyvn_push_tried")) return;
    sessionStorage.setItem("wyvn_push_tried", "1");
    enableWebPush().catch(() => {});
  }, [member]);

  // Auto-sync Strava when the app loads (once per session) so activities appear without manual sync.
  useEffect(() => {
    if (!member) return;
    if (sessionStorage.getItem("wyvn_strava_synced")) return;
    sessionStorage.setItem("wyvn_strava_synced", "1");
    api.stravaStatus()
      .then((s) => { if (s && s.connected) return api.stravaSync(); })
      .catch(() => {});
  }, [member]);

  // Unread notification badge — poll while signed in.
  useEffect(() => {
    if (!member) { setUnread(0); return; }
    const fetchUnread = () => api.notificationsUnread().then((r) => setUnread(r.unread || 0)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [member, page]);

  // Keyboard shortcut: press "N" to open Quick Add (when not typing).
  useEffect(() => {
    if (!member) return;
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); setQuickAdd(true); }
      if (e.key === "Escape") setQuickAdd(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [member]);

  const navigate = (p) => {
    const target = p === "checkin" ? "dashboard" : p;
    window.history.pushState(null, "", `#${target}`);
    setPage(target);
    window.scrollTo({ top: 0, behavior: "instant" });
    const main = document.getElementById("main-content");
    if (main) main.scrollTop = 0;
  };

  // Native deep links: open the right screen when the app is launched via a URL/link.
  useEffect(() => {
    if (!isNativeApp()) return;
    let listener;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        listener = await CapApp.addListener("appUrlOpen", ({ url }) => {
          try {
            const u = new URL(url);
            const ref = u.searchParams.get("ref");
            if (ref) sessionStorage.setItem("wyvn_ref", ref);
            const target = (u.hash || "").replace("#", "") || u.pathname.replace(/^\//, "");
            if (target) navigate(target);
          } catch (_) {}
        });
      } catch (_) {}
    })();
    return () => { try { listener && listener.remove(); } catch (_) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notification taps (push) → navigate to the linked screen.
  useEffect(() => {
    const h = (e) => {
      const t = String(e.detail || "").replace(/^https?:\/\/[^/]+/, "").replace(/^[/#]+/, "");
      if (t) navigate(t);
    };
    window.addEventListener("wyvn:navigate", h);
    return () => window.removeEventListener("wyvn:navigate", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Public "send love" page for a grieving member's invited loved ones (before anything else).
  if (careToken) return <CarePage token={careToken} />;

  if (showSplash) {
    return <SplashScreen onDone={() => { sessionStorage.setItem("aeva_splash_shown", "1"); setShowSplash(false); }} />;
  }

  // Public "send love" page for a grieving member's invited loved ones.
  if (careToken) return <CarePage token={careToken} />;

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink" data-testid="app-loading">
        <img src="/brand/wyvn-symbol.png" alt="" width={64} height={64} style={{ objectFit: "contain", animation: "aeva-aurora 1.6s ease-in-out infinite" }} />
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
      </div>
    );
  }

  // Password reset via emailed/dev link (works logged-in or out)
  if (resetToken) return <ResetPassword token={resetToken} onNavigate={navigate} onDone={() => setResetToken(null)} />;

  // Return from Stripe Checkout
  if (checkoutSession) return <Suspense fallback={<PageFallback />}><PaymentReturn sessionId={checkoutSession} onNavigate={navigate} /></Suspense>;

  // Public static pages accessible whether or not signed in
  const STATIC = {
    privacy: <Legal doc="privacy" onNavigate={navigate} />,
    terms: <Legal doc="terms" onNavigate={navigate} />,
    disclaimer: <Legal doc="disclaimer" onNavigate={navigate} />,
    contact: <Contact onNavigate={navigate} />,
    forgot: <ForgotPassword onNavigate={navigate} />,
  };
  if (STATIC[page]) return <Suspense fallback={<PageFallback />}>{STATIC[page]}</Suspense>;

  if (!member) {
    if (page === "signup") return <Signup onNavigate={navigate} />;
    if (page === "login") return <Login onNavigate={navigate} />;
    // Native app opens sign-in-first (clean); web keeps the marketing landing for visitors.
    return isNativeApp() ? <Login onNavigate={navigate} /> : <Landing onNavigate={navigate} />;
  }

  const appPage = PUBLIC.includes(page) ? "dashboard" : page;
  const gate = (feature, node) => <UpgradeGate feature={feature} onNavigate={navigate}>{node}</UpgradeGate>;
  const render = () => {
    switch (appPage) {
      case "coaches": return gate("coaches", <Coaches onNavigate={navigate} />);
      case "feed": return <Feed />;
      case "workouts": return <Workouts />;
      case "workout": return <WorkoutLogger />;
      case "nutrition": return <Nutrition />;
      case "weight": return <WeightTracker onNavigate={navigate} />;
      case "wellness": return gate("wellness", <Wellness />);
      case "cycle": return gate("cycle", <CycleTracker onNavigate={navigate} />);
      case "wearables": return gate("wearables", <Wearables />);
      case "passport": return gate("passport", <Passport />);
      case "leaderboard": return gate("leaderboard", <Leaderboard />);
      case "challenges": return gate("challenges", <Challenges onNavigate={navigate} />);
      case "badges": return <Badges />;
      case "community": return gate("community", <Community />);
      case "partner": return <Partner onNavigate={navigate} />;
      case "pregnancy": return gate("cycle", <Pregnancy />);
      case "family": return gate("family", <FamilyPlan />);
      case "referrals": return <Referrals />;
      case "halloffame": return <HallOfFame />;
      case "notifications": return <Notifications onRead={() => setUnread(0)} />;
      case "subscription": return <Subscription />;
      case "medical": return <MedicalProfile />;
      case "profile": return <Profile onNavigate={navigate} />;
      case "admin": return member.role === "admin" ? <Admin /> : <Dashboard onNavigate={navigate} />;
      default: return <Dashboard onNavigate={navigate} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-ink">
      <Sidebar currentPage={appPage} onNavigate={navigate} unread={unread} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onNavigate={navigate} onOpenMenu={() => setMenuOpen(true)} unread={unread} />
        <main id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden pb-24 lg:pb-0">
          <div key={`${appPage}-${refreshKey}`} className="animate-fade-in min-w-0 max-w-full">
            <Suspense fallback={<PageFallback />}>{render()}</Suspense>
          </div>
        </main>
      </div>
      <BottomTabBar currentPage={appPage} onNavigate={navigate} onQuickAdd={() => setQuickAdd(true)} />
      <DesktopQuickAddButton onClick={() => setQuickAdd(true)} />
      <MobileMenu open={menuOpen} currentPage={appPage} onClose={() => setMenuOpen(false)} onNavigate={navigate} unread={unread} />
      <QuickAddSheet open={quickAdd} onClose={() => setQuickAdd(false)} onNavigate={navigate} onQuickLog={(mode) => setQuickLog(mode)} />
      {quickLog && <QuickLogModal mode={quickLog} onClose={() => setQuickLog(null)} onDone={() => setRefreshKey((k) => k + 1)} />}
      {showIntro && <WelcomeIntro onDone={() => setShowIntro(false)} />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <div className="App">
        <AppContent />
        <Toaster position="top-center" theme="dark" />
      </div>
    </AuthProvider>
  );
}
